// URL regex pattern - for detecting URLs in messages
const URL_PATTERN = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;

// Media type extensions
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'mpeg', 'mpga'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'webm', 'amr'];
const DOCUMENT_EXTENSIONS = [
  'txt', 'md', 'markdown', 'pdf', 'html', 'xlsx', 
  'xls', 'docx', 'csv', 'eml', 'msg', 'pptx', 
  'ppt', 'xml', 'epub'
];

// Social media domains for detection
const SOCIAL_MEDIA_DOMAINS = [
  'instagram.com',
  'www.instagram.com',
  'tiktok.com',
  'www.tiktok.com',
  'youtube.com',
  'www.youtube.com',
  'youtu.be',
  'facebook.com',
  'www.facebook.com',
  'fb.com',
  'twitter.com',
  'x.com',
  'www.twitter.com',
  'www.x.com',
  'pinterest.com',
  'www.pinterest.com',
  'reddit.com',
  'www.reddit.com',
  'snapchat.com',
  'www.snapchat.com',
  'vimeo.com',
  'www.vimeo.com',
  'rutube.ru',
  'www.rutube.ru',
  'soundcloud.com',
  'www.soundcloud.com'
];

/**
 * Extract URLs from a text string
 * @param text The text to extract URLs from
 * @returns Array of URLs found in the text
 */
export function extractUrls(text: string): string[] {
  return text.match(URL_PATTERN) || [];
}

/**
 * Check if a string is a valid URL
 * @param urlString The string to check
 * @returns True if the string is a valid URL, false otherwise
 */
export function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
}

/**
 * Determines the file type from a URL based on the file extension or ending
 * @param url The URL to check
 * @returns The file type ('image', 'video', 'audio', 'document', 'custom') or null if not determinable
 */
export function getMediaTypeFromUrl(url: string): 'image' | 'video' | 'audio' | 'document' | 'custom' | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('.');
    const extension = pathParts.length > 1 ? pathParts[pathParts.length - 1].toLowerCase() : '';
    
    if (IMAGE_EXTENSIONS.includes(extension)) {
      return 'image';
    } else if (VIDEO_EXTENSIONS.includes(extension)) {
      return 'video';
    } else if (AUDIO_EXTENSIONS.includes(extension)) {
      return 'audio';
    } else if (DOCUMENT_EXTENSIONS.includes(extension)) {
      return 'document';
    } else {
      return 'custom';
    }
  } catch {
    return null;
  }
}

/**
 * Determines if a URL appears to be a media file that we should treat as a Dify media input
 * @param url The URL to check
 * @returns True if the URL appears to be a media file we should process
 */
export function isMediaUrl(url: string): boolean {
  const mediaType = getMediaTypeFromUrl(url);
  // Only process image and video URLs for now
  return mediaType === 'image' || mediaType === 'video';
}

/**
 * Detects if a URL is from a social media platform
 * @param url The URL to check
 * @returns True if the URL is from a social media platform
 */
export function isSocialMediaUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return SOCIAL_MEDIA_DOMAINS.includes(urlObj.hostname);
  } catch {
    return false;
  }
}

// Essential parameters to preserve by platform
const ESSENTIAL_PARAMS: Record<string, string[]> = {
  'youtube.com': ['v', 't', 'list', 'index', 'start'], // video ID, timestamp, playlist
  'www.youtube.com': ['v', 't', 'list', 'index', 'start'],
  'youtu.be': ['t', 'list'], // shortened URLs have video ID in path
  'facebook.com': ['fbid', 'id', 'story_fbid', 'v'], // post/photo/video IDs
  'www.facebook.com': ['fbid', 'id', 'story_fbid', 'v'],
  'fb.com': ['fbid', 'id', 'story_fbid', 'v'],
  'reddit.com': ['context'], // comment context
  'www.reddit.com': ['context'],
};

/**
 * Cleans a social media URL by removing tracking parameters while preserving essential ones
 * @param url The URL to clean
 * @returns The cleaned URL with tracking parameters removed
 */
export function cleanSocialMediaUrl(url: string): string {
  try {
    const urlObj = new URL(url);

    // Only clean URLs from social media platforms
    if (!SOCIAL_MEDIA_DOMAINS.includes(urlObj.hostname)) {
      return url;
    }

    // Get essential parameters for this platform
    const essentialParams = ESSENTIAL_PARAMS[urlObj.hostname] || [];

    // Clean query parameters
    if (urlObj.search) {
      const params = new URLSearchParams(urlObj.search);
      const cleanedParams = new URLSearchParams();

      // Keep only essential parameters
      for (const [key, value] of params.entries()) {
        const isEssential = essentialParams.includes(key);

        if (isEssential) {
          cleanedParams.append(key, value);
        }
      }

      // Update URL with cleaned parameters
      urlObj.search = cleanedParams.toString();
    }

    // Always remove hash fragments
    urlObj.hash = '';

    // Remove trailing slash if present, but not for the root path
    if (urlObj.pathname.length > 1 && urlObj.pathname.endsWith('/')) {
      urlObj.pathname = urlObj.pathname.slice(0, -1);
    }

    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Cleans all social media URLs found in a text string
 * @param text The text containing URLs
 * @returns The text with cleaned URLs
 */
export function cleanSocialMediaUrlsInText(text: string): string {
  // Single-pass replacement using callback function
  return text.replace(URL_PATTERN, (url) => cleanSocialMediaUrl(url));
}

/**
 * Processes a social media URL through the extraction service
 * @param url The social media URL to process
 * @returns The extracted direct media URL or the original URL if extraction fails
 */
export async function extractSocialMediaUrl(url: string): Promise<string> {
  if (!isSocialMediaUrl(url)) {
    return url; // Not a social media URL, return as is
  }

  try {
    const response = await fetch('/api/files/social-media-extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      console.warn(`Social media extraction failed for ${url}:`, await response.text());
      return url; // Return original URL if extraction fails
    }

    const result = await response.json();
    return result.extractedUrl || url; // Use extracted URL or fallback to original
  } catch (error) {
    console.error(`Error extracting social media URL ${url}:`, error);
    return url; // Return original URL on error
  }
}

/**
 * Returns a hint about whether a YouTube URL is a Short, long-form video, or unknown.
 *
 * Detection rules (in order):
 * 1. URL path contains /shorts/  → 'short'
 * 2. list param starts with UUSH or UUPS → 'short' (YouTube Shorts playlists)
 * 3. list param starts with UULF or UULP → 'long' (YouTube long-form playlists)
 * 4. Otherwise → 'unknown' (requires API call to determine)
 *
 * Background: YouTube auto-generates typed playlist IDs from a channel ID (UCxxxxxxx)
 * by replacing the UC prefix: UUSH=recent Shorts, UUPS=popular Shorts,
 * UULF=recent long-form, UULP=popular long-form.
 */
export function getYouTubeShortUrlHint(url: string): 'short' | 'long' | 'unknown' {
  try {
    const urlObj = new URL(url);

    if (urlObj.pathname.includes('/shorts/')) {
      return 'short';
    }

    const list = urlObj.searchParams.get('list') ?? '';
    if (list.startsWith('UUSH') || list.startsWith('UUPS')) {
      return 'short';
    }
    if (list.startsWith('UULF') || list.startsWith('UULP')) {
      return 'long';
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Detects if a URL is a TikTok short URL (vt.tiktok.com or vm.tiktok.com)
 * @param url The URL to check
 * @returns True if the URL is a TikTok short URL
 */
export function isTikTokShortUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'vt.tiktok.com' ||
           urlObj.hostname === 'vm.tiktok.com';
  } catch {
    return false;
  }
}

/**
 * Resolves a TikTok short URL to its full URL by following redirects
 * Uses backend proxy to bypass CORS restrictions
 * @param shortUrl The TikTok short URL to resolve
 * @returns The full TikTok URL after following redirects
 */
export async function resolveTikTokShortUrl(shortUrl: string): Promise<string> {
  if (!isTikTokShortUrl(shortUrl)) {
    return shortUrl;
  }

  try {
    // Call backend API to resolve URL (bypasses CORS)
    const response = await fetch('/api/tiktok/resolve-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: shortUrl }),
    });

    if (!response.ok) {
      console.error(`[TikTok URL Resolution] API error:`, response.status, await response.text());
      return shortUrl;
    }

    const data = await response.json();
    const fullUrl = data.resolvedUrl || shortUrl;

    if (data.wasResolved) {
      console.log(`[TikTok URL Resolution] ${shortUrl} → ${fullUrl}`);
    }

    return fullUrl;
  } catch (error) {
    console.error(`[TikTok URL Resolution] Failed to resolve ${shortUrl}:`, error);
    return shortUrl; // Fallback to original URL on error
  }
}

/**
 * Resolves all TikTok short URLs found in a text string
 * @param text The text containing TikTok short URLs
 * @returns The text with all TikTok short URLs resolved to full URLs
 */
export async function resolveTikTokShortUrlsInText(text: string): Promise<string> {
  const urls = extractUrls(text);
  const tiktokShortUrls = Array.from(new Set(urls.filter(isTikTokShortUrl))).slice(0, 10);

  if (tiktokShortUrls.length === 0) {
    return text;
  }

  // Resolve all TikTok short URLs in parallel
  const resolutions = await Promise.all(
    tiktokShortUrls.map(async (shortUrl) => {
      const resolved = await resolveTikTokShortUrl(shortUrl);
      return {
        original: shortUrl,
        resolved,
      };
    })
  );

  // Replace all short URLs with resolved full URLs
  // Sort by length descending to prevent replacing substrings of longer URLs
  const sortedResolutions = [...resolutions].sort(
    (a, b) => b.original.length - a.original.length
  );

  let result = text;
  for (const { original, resolved } of sortedResolutions) {
    if (original !== resolved) {
      // Use split/join to replace all occurrences safely
      result = result.split(original).join(resolved);
    }
  }

  return result;
}